import { apiConfig } from "../config/api";
import { getMockData } from "../mocks/data";
import {
  ParseCSVInBucketResponse,
  ProductQualityMetric,
  Filters,
  MetaField,
  EnrichmentConfig,
  UpdateField,
  GetCategoryFilterOptionsResponse,
  CategoryGroupItem,
} from "../types/api";
import { faker } from "@faker-js/faker";
import { transformSortValues, transformDateValues } from "./utils";
import { JobStatusResponse } from "../admin/dashboards/types";
import { QualityScore } from "../types/api";
import { GetProductsResponse, AssitantSuggestionResponse } from "../types/api";
import * as Sentry from "@sentry/browser";

// Add type definition at the top of file
type ApiResponse<T> = {
  success: boolean;
  data?: T;
  error?: {
    status: number;
    message: string;
  };
};

export class ApiService {
  private static instance: ApiService;
  private baseUrl: string;
  private isMock: boolean;

  private constructor() {
    this.baseUrl = apiConfig.baseUrl;
    this.isMock = apiConfig.isMock;
  }

  static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  private async fetchWrapper<T>(
    endpoint: string,
    options?: RequestInit,
    isMock?: boolean,
    multiPart?: boolean
  ): Promise<ApiResponse<T>> {
    // console.log(`API call: ${endpoint}`, options);
    // console.log(`Body: `, typeof options?.body === 'string' ? JSON.parse(options.body) : {});
    if (isMock) {
      return this.getMockResponse<T>(endpoint);
    }

    Sentry.addBreadcrumb({
      category: "api",
      message: `API call: ${endpoint}`,
      data: options,
    });

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        ...(!multiPart && { "Content-Type": "application/json" }),
        ...options?.headers,
      },
    });

    if (!response.ok) {
      const detail = await response.text();
      Sentry.captureMessage(`API error: ${endpoint}`, {
        extra: {
          status: response.status,
          detail,
        },
      });
      return {
        success: false,
        error: {
          status: response.status,
          message: detail,
        },
      };
    }

    const data = await response.json();

    Sentry.addBreadcrumb({
      category: "api",
      message: `API response: ${endpoint}`,
      data,
    });

    // console.log(`API response: ${endpoint}`, data);
    return {
      success: true,
      data,
    };
  }

  private getMockResponse<T>(endpoint: string): Promise<ApiResponse<T>> {
    const mockData = getMockData();

    const mockResponses: Record<string, any> = {
      "/util/parse_csv_in_bucket": mockData.parseCSVResponse,
      "/view/product_details_and_metrics": {
        products: mockData.products,
        total_count: mockData.products.length,
        offset: 0,
      },
      "/pre_enrichment/infer_category_metafields": {
        meta_fields: mockData.metaFields,
      },
      "/import/excel_to_internal_table": {
        job_id: "123",
        status: "running",
      },
    };

    const [path, queryString] = endpoint.split("?");
    const queryParams = new URLSearchParams(queryString);

    return new Promise((resolve) => {
      setTimeout(
        () => {
          resolve(
            (mockResponses[path]
              ? {
                  data: mockResponses[path],
                  success: true,
                }
              : { success: true }) as ApiResponse<T>
          );
        },
        faker.number.int({ min: 2000, max: 3000 })
      ); // Random realistic delay
    });
  }

  async parseCSVInBucket(
    fileUri: string
  ): Promise<ApiResponse<ParseCSVInBucketResponse>> {
    return this.fetchWrapper<ParseCSVInBucketResponse>(
      `/util/parse_csv_in_bucket?file_uri=${encodeURIComponent(fileUri)}`
    );
  }

  async runEnrichment({
    accountId,
    collectionName,
    tableId,
    filter,
    config,
  }: {
    accountId: string;
    collectionName: string;
    tableId: string;
    filter: Filters;
    config: EnrichmentConfig;
  }): Promise<ApiResponse<void>> {
    const updatedFilters = {
      ...filter,
      date_range: transformDateValues(filter.date_range),
    };

    return this.fetchWrapper(
      `/enrichment/run_enrichment?account_id=${accountId}&collection_name=${collectionName}&table_id=${tableId}`,
      {
        method: "POST",
        body: JSON.stringify({
          filters: updatedFilters,
          config: config,
        }),
      }
    );
  }

  async getProductList({
    accountId,
    collectionName,
    tableId,
    limit,
    offset,
    sortField,
    filters,
    metaFields,
    enrichmentJobId,
  }: {
    accountId: string;
    collectionName: string;
    tableId: string;
    limit: number;
    offset: number;
    sortField: string;
    filters: Filters;
    metaFields: MetaField[];
    enrichmentJobId?: string;
  }): Promise<ApiResponse<GetProductsResponse>> {
    const sort = transformSortValues(sortField);
    let updatedFilters = {
      ...filters,
      date_range: transformDateValues(filters.date_range),
    };
    let url = `/view/product_details?account_id=${accountId}&table_id=${tableId}`;
    if (enrichmentJobId) {
      url = url + `&enrichment_job_id=${enrichmentJobId}`;
    }

    return this.fetchWrapper<GetProductsResponse>(url, {
      method: "POST",
      body: JSON.stringify({
        limit: limit,
        offset: offset,
        sort_field: sort.sortField,
        sort_order: sort.sortOrder,
        filters: updatedFilters,
        meta_fields: metaFields,
      }),
    });
  }

  async postPreEnrichmentImageBucket({ tableId }: { tableId: string }) {
    return this.fetchWrapper<{
      job_id: string;
    }>(`/pre_enrichment/internal_table_image_bucket?table_id=${tableId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({}),
    });
  }

  async postEnrichTitleAndDescription({
    tableId,
    imageUriTable,
  }: {
    tableId: string;
    imageUriTable: string;
  }) {
    return this.fetchWrapper<{
      job_id: string;
    }>(
      `/enrichment/enrich_title_and_description?table_id=${tableId}&image_uri_table=${imageUriTable}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      }
    );
  }

  async postEnrich({
    tableId,
    config,
    product_ids,
    filters,
  }: {
    tableId: string;
    config: EnrichmentConfig;
    product_ids?: string[];
    filters?: Filters;
  }) {
    let updatedFilters = {
      ...filters,
      date_range: transformDateValues(filters?.date_range),
    };
    return this.fetchWrapper<{
      job_id: string;
    }>(`/enrichment/run_enrichment?table_id=${tableId}&use_batch_api=false`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        config: config,
        ...(product_ids && { product_ids }),
        ...(filters && { filters: updatedFilters }),
      }),
    });
  }

  async postUpdateEnrichedCollection({
    job_id,
    table_id,
    update_fields,
  }: {
    job_id: string;
    table_id: string;
    update_fields: UpdateField[];
  }) {
    return this.fetchWrapper<{
      job_id: string;
    }>(
      `/enrichment/update_enriched_collection?job_id=${job_id}&table_id=${table_id}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          update_fields,
        }),
      }
    );
  }

  async getContentQualityScore({
    tableId,
    enrichmentJobId,
  }: {
    tableId: string;
    enrichmentJobId?: string;
  }) {
    let url = `/view/content_quality_score?table_id=${tableId}`;
    if (enrichmentJobId) {
      url = url + `&enrichment_job_id=${enrichmentJobId}`;
    }
    return this.fetchWrapper<QualityScore>(url);
  }

  async getPreScoreTable({ tableId }: { tableId: string }) {
    return this.fetchWrapper(
      `/scoring/get_score_table?table_id=${tableId}&score_name=pre_score`
    );
  }

  async getPostScoreTable({ tableId }: { tableId: string }) {
    return this.fetchWrapper(
      `/scoring/get_score_table?table_id=${tableId}&score_name=post_score`
    );
  }

  async getAssistantSuggestions({
    tableId,
    productIds,
    filters,
  }: {
    tableId: string;
    productIds?: string[];
    filters?: Filters;
  }) {
    return this.fetchWrapper<AssitantSuggestionResponse>(
      `/view/get_assistant_content_style_config?table_id=${tableId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...(filters && { filters: filters }),
          ...(productIds && { product_ids: productIds }),
        }),
      }
    );
  }

  async getJobStatus(jobId: string) {
    return this.fetchWrapper<JobStatusResponse>(`/jobs/${jobId}`);
  }

  async getFileFromBucket(gsUri: string) {
    return this.fetchWrapper<Blob>(
      `/utils/get_file_from_bucket?gs_uri=${gsUri}`
    );
  }

  async exportEnrichmentToShopify({
    input_table_id,
    enrichment_job_id,
    shopify_store_name,
    fields_to_export,
  }: {
    input_table_id: string;
    enrichment_job_id: string;
    shopify_store_name: string;
    fields_to_export?: string[];
  }) {
    return this.fetchWrapper<{
      job_id: string;
      status: string;
      progress: number;
      response_type: string;
      response: string;
      message: string;
      last_updated: 0;
    }>(
      `/export/products_field_enrichment_to_shopify?input_table_id=${input_table_id}&enrichment_job_id=${enrichment_job_id}&shopify_store_name=${shopify_store_name}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...(fields_to_export && { fields_to_export }),
        }),
      }
    );
  }

  async exportToCSV({
    inputTableId,
    enrichmentJobId,
    filters,
    productIds,
    format = "csv",
  }: {
    enrichmentJobId: string;
    inputTableId: string;
    format: "csv" | "xlsx" | "jsonl";
    filters?: Filters;
    productIds?: string[];
  }) {
    return this.fetchWrapper<{
      job_id: string;
      status: string;
    }>(
      `/export/products_field_enrichment_to_csv?input_table_id=${inputTableId}&enrichment_job_id=${enrichmentJobId}&export_format=${format}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...(filters && { filters }),
          ...(productIds && { product_ids: productIds }),
        }),
      }
    );
  }

  async postShopifyToInternalTable({
    account_id,
    collection_name,
    shopify_store_name,
  }: {
    account_id: string;
    collection_name: string;
    shopify_store_name: string;
  }) {
    return this.fetchWrapper<{
      job_id: string;
      status: string;
    }>(
      `/import/shopify_to_internal_table?account_id=${account_id}&collection_name=${encodeURIComponent(
        collection_name
      )}&shopify_store_name=${encodeURIComponent(shopify_store_name)}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      }
    );
  }

  async cancelJob(jobId: string) {
    return this.fetchWrapper<{ message: string }>(
      `/common/cancel_job/${jobId}`,
      {
        method: "GET",
      }
    );
  }

  async postExcelToInternalTable({
    account_id,
    collection_name,
    file,
  }: {
    account_id: string;
    collection_name: string;
    file: File;
  }) {
    const formData = new FormData();
    formData.append("file", file);
    return this.fetchWrapper<{
      job_id: string;
      status: string;
      message?: string;
    }>(
      `/import/excel_to_internal_table?account_id=${account_id}&collection_name=${encodeURIComponent(
        collection_name
      )}}`,
      {
        method: "POST",
        body: formData,
      },
      false,
      true
    );
  }

  async getCategoryFilterOptions({
    tableId,
  }: {
    tableId: string;
  }): Promise<ApiResponse<GetCategoryFilterOptionsResponse>> {
    let url = `/view/get_category_filter_options?table_id=${tableId}`;

    return this.fetchWrapper<GetCategoryFilterOptionsResponse>(url, {
      method: "POST",
      body: JSON.stringify({}),
    });
  }
}

// Usage example
export const api = ApiService.getInstance();
